import React, { useEffect, useRef, useState } from "react";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import { Form, Formik, getIn } from "formik";
import IconSuccess from "../../../assets/icons/IconSuccess";
import ButtonBack from "../../Buttons/ButtonBack";
import ButtonContainer from "../../Buttons/ButtonContainer";
import ButtonNext from "../../Buttons/ButtonNext";
import ButtonSubmit from "../../Buttons/ButtonSubmit";
import {
  aiExperienceOptions,
  expectationOptions,
  getOptionLabel,
  obstacleOptions,
  occupationOptions,
  regionOptions,
} from "./data/fieldOptions";
import initialValues from "./data/initialValues";
import ConfirmationStep from "./steps/ConfirmationStep";
import DiagnosisStep from "./steps/DiagnosisStep";
import PersonalDataStep from "./steps/PersonalDataStep";
import ProfileStep from "./steps/ProfileStep";
import getCaptchaToken from "./utils/getCaptchaToken";
import { stepFieldNames, stepSchemas } from "./validation/stepSchemas";
import axios from "axios";

const steps = [
  PersonalDataStep,
  ProfileStep,
  DiagnosisStep,
  ConfirmationStep,
];

const SUCCESS_MESSAGE_DURATION = 5000;

const SubmissionSuccess = () => (
  <div className="registration-success" role="status" aria-live="polite">
    <IconSuccess />
    <h3>Datos enviados</h3>
    <p>Muchas gracias por registrarte.</p>
    <small>El formulario se reiniciará en unos segundos.</small>
  </div>
);

const RegistrationForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [submissionSucceeded, setSubmissionSucceeded] = useState(false);
  const submissionInProgress = useRef(false);
  const successTimeout = useRef(null);
  const { executeV3 } = useGoogleReCaptcha();
  const ActiveStep = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  useEffect(
    () => () => {
      if (successTimeout.current) {
        window.clearTimeout(successTimeout.current);
      }
    },
    []
  );

  const handleBack = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const handleNext = async (formik) => {
    const currentFields = stepFieldNames[currentStep];
    const nextTouched = currentFields.reduce(
      (touchedFields, fieldName) => ({
        ...touchedFields,
        [fieldName]: true,
      }),
      formik.touched
    );

    formik.setTouched(nextTouched, false);
    const errors = await formik.validateForm();
    const currentStepHasErrors = currentFields.some((fieldName) =>
      getIn(errors, fieldName)
    );

    if (!currentStepHasErrors) {
      setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
    }
  };

  const handleFinalSubmit = async (values, { resetForm, setSubmitting }) => {
    if (submissionInProgress.current) {
      return;
    }

    submissionInProgress.current = true;
    const registrationData = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      region: getOptionLabel(regionOptions, values.region),
      occupation: getOptionLabel(occupationOptions, values.occupation),
      project_name: values.project_name,
      obstacle: getOptionLabel(obstacleOptions, values.obstacle),
      experience: getOptionLabel(aiExperienceOptions, values.experience),
      expectation: getOptionLabel(expectationOptions, values.expectation),
    };

    console.log("Payload previo a reCAPTCHA:", registrationData);

    try {
      let token;

      try {
        token = await getCaptchaToken(executeV3);
      } catch (error) {
        console.error("No se pudo validar el envío con reCAPTCHA", error);
        return;
      }

      const payload = { ...registrationData, token };

      const url = "https://form-demo-912931479763.southamerica-west1.run.app";
      try {
        const response = await axios.post(url, payload);
        console.log(response);
      } catch (error) {
        console.error("No se pudieron enviar los datos al servidor", error);
        return;
      }

      setSubmissionSucceeded(true);
      successTimeout.current = window.setTimeout(() => {
        resetForm();
        setCurrentStep(0);
        setSubmissionSucceeded(false);
        successTimeout.current = null;
      }, SUCCESS_MESSAGE_DURATION);
    } finally {
      submissionInProgress.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={stepSchemas[currentStep]}
      onSubmit={handleFinalSubmit}
    >
      {(formik) =>
        submissionSucceeded ? (
          <SubmissionSuccess />
        ) : (
          <Form autoComplete="off" noValidate>
            <ActiveStep />
            {!formik.isSubmitting && (
              <ButtonContainer>
                {currentStep > 0 && <ButtonBack onClick={handleBack} />}
                {isLastStep ? (
                  <ButtonSubmit />
                ) : (
                  <ButtonNext onClick={() => handleNext(formik)} />
                )}
              </ButtonContainer>
            )}
          </Form>
        )
      }
    </Formik>
  );
};

export default RegistrationForm;
